import { useEffect, useState } from 'react';
import { Download, Film, Upload, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { api } from '@/lib/api';
import type { Property } from '@/lib/types';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	property: Property | null; // null = 新增
	onSubmit: (data: Partial<Property>, videoFile: File | null, removeVideo: boolean) => Promise<void> | void;
	saving?: boolean;
}

const MAX_VIDEO_SIZE = 100 * 1024 * 1024;

const EMPTY: Partial<Property> = {
	room_number: '',
	status: '出售',
	is_active: true,
	community_name: '',
	floor: '',
	area: undefined,
	decoration: '',
	garage: '',
	build_time: '',
	school_district: '',
	total_price: undefined,
	remarks: '',
};

export function PropertyForm({ open, onOpenChange, property, onSubmit, saving }: Props) {
	const [form, setForm] = useState<Partial<Property>>(EMPTY);
	const [videoFile, setVideoFile] = useState<File | null>(null);
	const [removeVideo, setRemoveVideo] = useState(false);
	const [videoError, setVideoError] = useState('');

	useEffect(() => {
		setForm(property ? { ...property } : EMPTY);
		setVideoFile(null);
		setRemoveVideo(false);
		setVideoError('');
	}, [property, open]);

	const isRent = form.status === '出租' || form.status === '已租';
	const priceLabel = isRent ? '月租 (元)' : '总价 (万元)';

	const handle = <K extends keyof Property>(key: K, value: Property[K]) =>
		setForm((f) => ({ ...f, [key]: value }));

	const submit = async (e: React.FormEvent) => {
		e.preventDefault();
		await onSubmit(form, videoFile, removeVideo);
	};

	const existingVideoVisible = Boolean(property?.video_key && !removeVideo && !videoFile);

	const handleVideoFile = (file: File | null) => {
		setVideoError('');
		if (!file) {
			setVideoFile(null);
			return;
		}
		if (!file.type.startsWith('video/')) {
			setVideoError('请选择视频文件');
			return;
		}
		if (file.size > MAX_VIDEO_SIZE) {
			setVideoError('视频不能超过 100MB');
			return;
		}
		setRemoveVideo(false);
		setVideoFile(file);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-3xl">
				<form onSubmit={submit} className="contents">
					<DialogHeader>
						<DialogTitle>{property ? '编辑房源' : '新增房源'}</DialogTitle>
					</DialogHeader>
					<DialogBody>
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-4">
							<Field label="房号 *">
								<Input value={form.room_number ?? ''} onChange={(e) => handle('room_number', e.target.value)} required />
							</Field>
							<Field label="状态 *">
								<Select value={form.status} onChange={(e) => handle('status', e.target.value as Property['status'])} required>
									<option value="出售">出售</option>
									<option value="出租">出租</option>
									<option value="已售">已售</option>
									<option value="已租">已租</option>
								</Select>
							</Field>
							<Field label="是否有效">
								<Select
									value={String(form.is_active)}
									onChange={(e) => handle('is_active', e.target.value === 'true')}
								>
									<option value="true">是</option>
									<option value="false">否</option>
								</Select>
							</Field>

							<Field label="小区名称 *" className="sm:col-span-2">
								<Input value={form.community_name ?? ''} onChange={(e) => handle('community_name', e.target.value)} required />
							</Field>
							<Field label="楼层">
								<Input value={form.floor ?? ''} onChange={(e) => handle('floor', e.target.value)} placeholder="如 6/30" />
							</Field>

							<Field label="面积 (㎡)">
								<Input
									type="number"
									step="0.1"
									value={form.area ?? ''}
									onChange={(e) => handle('area', e.target.value === '' ? null : Number(e.target.value))}
								/>
							</Field>
							<Field label="装潢">
								<Input value={form.decoration ?? ''} onChange={(e) => handle('decoration', e.target.value)} placeholder="精装 / 中装 / 毛坯" />
							</Field>
							<Field label="车库">
								<Input value={form.garage ?? ''} onChange={(e) => handle('garage', e.target.value)} />
							</Field>

							<Field label="建房时间">
								<Input value={form.build_time ?? ''} onChange={(e) => handle('build_time', e.target.value)} placeholder="如 2019-12" />
							</Field>
							<Field label="学区" className="sm:col-span-2">
								<Input value={form.school_district ?? ''} onChange={(e) => handle('school_district', e.target.value)} />
							</Field>

							<Field label={priceLabel}>
								<Input
									type="number"
									step="0.1"
									value={form.total_price ?? ''}
									onChange={(e) => handle('total_price', e.target.value === '' ? null : Number(e.target.value))}
								/>
							</Field>
							<Field label="备注" className="sm:col-span-3">
								<Textarea value={form.remarks ?? ''} onChange={(e) => handle('remarks', e.target.value)} rows={2} />
							</Field>
							<Field label="视频" className="sm:col-span-3">
								<div className="rounded-md border border-line bg-white p-3">
									<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
										<label className="inline-flex w-fit cursor-pointer items-center gap-1.5 rounded-md border border-line px-2.5 py-1.5 text-[16px] text-ink-2 hover:bg-[#f4f4f3]">
											<Upload size={13} />
											选择视频
											<input
												type="file"
												accept="video/*"
												className="hidden"
												onChange={(e) => handleVideoFile(e.target.files?.[0] ?? null)}
											/>
										</label>
										{videoFile ? (
											<div className="flex min-w-0 flex-1 items-center gap-2 text-[16px] text-ink-2">
												<Film size={13} className="shrink-0 text-ink-3" />
												<span className="truncate">{videoFile.name}</span>
												<button
													type="button"
													className="icon-btn shrink-0"
													title="移除已选择视频"
													onClick={() => setVideoFile(null)}
												>
													<X size={13} />
												</button>
											</div>
										) : existingVideoVisible ? (
											<div className="flex min-w-0 flex-1 items-center gap-2 text-[16px] text-ink-2">
												<Film size={13} className="shrink-0 text-ink-3" />
												<span className="truncate">{property?.video_name || '已上传视频'}</span>
												<a
													className="icon-btn shrink-0"
													title="下载视频"
													href={api.getPropertyVideoUrl(property!.id)}
												>
													<Download size={13} />
												</a>
												<button
													type="button"
													className="icon-btn shrink-0 hover:!bg-red-50 hover:!text-danger"
													title="移除视频"
													onClick={() => setRemoveVideo(true)}
												>
													<X size={13} />
												</button>
											</div>
										) : (
											<span className="text-[15.5px] text-ink-3">未上传视频</span>
										)}
									</div>
									{removeVideo && !videoFile && (
										<p className="mt-2 text-[15.5px] text-ink-3">保存后会移除当前视频。</p>
									)}
									{videoError && <p className="mt-2 text-[15.5px] text-danger">{videoError}</p>}
								</div>
							</Field>
						</div>
					</DialogBody>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>取消</Button>
						<Button type="submit" variant="primary" disabled={saving}>
							{saving ? '保存中...' : '保存'}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
	return (
		<label className={`flex flex-col gap-1 ${className ?? ''}`}>
			<span className="text-[15px] text-ink-3 font-medium">{label}</span>
			{children}
		</label>
	);
}
